import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';

/**
 * <cos-not-found> — page-level 404 / record-not-found surface.
 *
 * Stage 70.1 extended this in place (it predates the stage as the `/*`
 * catch-all route in app.ts). Two variants:
 *
 *   - variant='page' (default) — the route-level 404. With zero attributes
 *     it renders exactly the pre-70.1 output ("Page not found" + back to
 *     dashboard), so the existing `/*` wiring is unchanged.
 *   - variant='record' — for an invalid /partners/:id, /tickets/:id, etc.
 *     Shows the 404 code prominently + standardized copy ("This record
 *     doesn't exist or you don't have access") + a back-to-list action.
 *
 * Every string is overridable via props for one-off callers.
 *
 * Props:
 *   - variant: 'page' | 'record' (default 'page')
 *   - code: string (default '404') — shown prominently in the record variant
 *   - title?: string — overrides the per-variant default heading
 *   - description?: string — overrides the per-variant default copy
 *   - backHref: string (default '/dashboard')
 *   - backLabel?: string — overrides the derived back-link label
 */
@customElement('cos-not-found')
export class CosNotFound extends LitElement {
	@property({ type: String }) variant: 'page' | 'record' = 'page';
	@property({ type: String }) code = '404';
	@property({ type: String }) title = '';
	@property({ type: String }) description = '';
	@property({ type: String, attribute: 'back-href' }) backHref = '/dashboard';
	@property({ type: String, attribute: 'back-label' }) backLabel = '';

	static styles = css`
		:host {
			display: flex;
			align-items: center;
			justify-content: center;
			min-height: 60vh;
			text-align: center;
		}
		.box {
			max-width: 420px;
			padding: var(--cos-space-8);
		}
		.code {
			font-size: var(--cos-font-size-3xl);
			font-weight: var(--cos-font-weight-semibold);
			color: var(--cos-text-tertiary);
			letter-spacing: var(--cos-letter-spacing-wide);
			line-height: 1;
			margin-bottom: var(--cos-space-2);
		}
		h1 {
			font-size: var(--cos-font-size-3xl);
			color: var(--cos-text-primary);
			margin-bottom: var(--cos-space-2);
		}
		/* In the record variant the prominent 404 owns the visual weight,
		   so the title steps down a notch. The page variant keeps its
		   pre-70.1 3xl heading. */
		:host([variant='record']) h1 {
			font-size: var(--cos-font-size-2xl);
		}
		p {
			color: var(--cos-text-secondary);
			margin-bottom: var(--cos-space-4);
		}
		a {
			color: var(--cos-brand-700);
			text-decoration: none;
			font-weight: var(--cos-font-weight-medium);
		}
		a:hover {
			text-decoration: underline;
		}
	`;

	private _title(): string {
		if (this.title) return this.title;
		return this.variant === 'record' ? 'Record not found' : 'Page not found';
	}

	private _description(): string {
		if (this.description) return this.description;
		return this.variant === 'record'
			? "This record doesn't exist or you don't have access."
			: "The route you tried to reach doesn't exist.";
	}

	private _backLabel(): string {
		if (this.backLabel) return this.backLabel;
		// Preserve the pre-70.1 default label for the dashboard target;
		// otherwise a generic "Back".
		return this.backHref === '/dashboard' ? '← Back to dashboard' : '← Back';
	}

	render() {
		return html`
			<div class="box" role="alert">
				${this.variant === 'record' ? html`<div class="code">${this.code}</div>` : ''}
				<h1>${this._title()}</h1>
				<p>${this._description()}</p>
				<a href=${this.backHref}>${this._backLabel()}</a>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'cos-not-found': CosNotFound;
	}
}
